import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import BreadCrumb from "../../breadcrumb";
import Header from "../../DashboardNavigation/header";
import Sidebar from "../../DashboardNavigation/sidebar";
import { Button } from "../../ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import { Heading } from "../../ui/heading";
import { Input } from "../../ui/input";
import { Separator } from "../../ui/separator";
import {
  BudgetFormValues,
  budgetSchema,
  updatedBudgetSchema,
  UpdatedBudgetSchema,
} from "./budget-zod.schema";

import PdfFileUpload from "../../FileUpload/PdfFileUpload";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  getBudgetById,
  handleCreateBudget,
  handleUpdateBudgets,
} from "../../../API/Budget/budgetService";

export const BudgetForm: React.ComponentType<any> = () => {
  const uploadUrl = process.env.REACT_APP_FILE_UPLOAD;

  const router = useNavigate();
  const { id } = useParams();
  const initialData = id ? parseInt(id) : undefined;
  const [loading, setLoading] = useState(false);
  const title = initialData
    ? "Modifier le budget"
    : "Ajouter un nouveau budget";
  const description = initialData ? "Modifier le budget" : "";
  const action = initialData ? "Mise à jour" : "Ajouter";

  const form = useForm<BudgetFormValues | UpdatedBudgetSchema>({
    resolver: zodResolver(initialData ? updatedBudgetSchema : budgetSchema),
  });

  const onSubmit = async () => {
    setLoading(true);
    const formData = form.getValues();
    try {
      if (initialData) {
        await handleUpdateBudgets(initialData, formData, router);
      } else {
        await handleCreateBudget(formData, router);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const [data, setData] = useState<any>();
  useEffect(() => {
    const fetchData = async () => {
      if (initialData) {
        const budgetData = await getBudgetById(initialData);

        setData(budgetData);
        form.reset({
          title: budgetData.title,
          category: budgetData.category,
          file: budgetData.file
            ? {
                fileName: budgetData.file,
                name: budgetData.file,
                fileSize: 0,
                size: 0,
                key: budgetData.file,
              }
            : undefined,
        });
      }
    };

    if (initialData) {
      fetchData();
    }
  }, [initialData]);

  const breadcrumbItems = [
    { title: "Presse", link: "/admin-dashboard/budgets" },
    {
      title: initialData ? "Mise à jour" : "Ajouter",
      link: "/admin-dashboard/budget/ajouter",
    },
  ];

  const categories = [
    { id: 1, designation: "Comptes Administratifs", value: "administratifs" },
    { id: 2, designation: "Décision modificative", value: "modificative" },
    {
      id: 3,
      designation: "Rapports d’orientation budgétaires",
      value: "budgetaires",
    },
    { id: 4, designation: "Budget primitif", value: "primitif" },
  ];

  return (
    <>
      <Header />
      <div className="flex h-screen ">
        <Sidebar />
        <div className="w-full p-10">
          <BreadCrumb items={breadcrumbItems} />
          <div className="flex items-center justify-between">
            <Heading title={title} description={description} />
          </div>
          <Separator />
          <Form {...form}>
            <form
              className="w-full space-y-8 overflow-y-auto"
              onSubmit={form.handleSubmit(onSubmit)}
              encType="multipart/form-data"
            >
              <div className="flex justify-between">
                <div className="w-1/2">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Titre </FormLabel>
                        <FormControl>
                          <Input
                            disabled={loading}
                            placeholder={"Titre..."}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="w-1/4">
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Catégorie</FormLabel>
                        <Select
                          disabled={loading}
                          onValueChange={field.onChange}
                          value={field.value}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue
                                defaultValue={field.value}
                                placeholder="Sélectionner catégorie"
                              />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {categories?.map((cat) => (
                              <SelectItem key={cat.id} value={cat.value}>
                                {cat.designation}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="w-full ">
                {data?.file && (
                  <div className="mt-4 mb-4">
                    <a
                      href={`${uploadUrl}/budget/${data.category}/${data.file}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 underline"
                    >
                      Consulter l'ancien PDF
                    </a>
                  </div>
                )}

                <FormField
                  control={form.control}
                  name="file"
                  render={() => (
                    <FormItem>
                      <FormControl>
                        <PdfFileUpload
                          onChange={(files) => {
                            const file = files[0];
                            if (file) {
                              const mappedFile = {
                                key: file.id,
                                fileName: file.fileName,
                                name: file.fileName,
                                fileSize: parseInt(file.fileSize),
                                size: parseInt(file.fileSize),
                                file: file.file,
                              };
                              form.setValue("file", mappedFile);
                            }
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  disabled={loading}
                  isLoading={loading}
                  className="ml-auto "
                  type="submit"
                >
                  {action}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </>
  );
};
